// eslint-disable-next-line @typescript-eslint/triple-slash-reference
/// <reference path="citation-js.d.ts" />

import cloneDeep from "lodash/cloneDeep.js";
import RIS from "./ris.js";
// eslint-disable-next-line import/namespace
import {Cite} from "@citation-js/core";
import "@citation-js/plugin-ris";
import "@citation-js/plugin-zotero-translation-server";
import "@citation-js/plugin-doi";
import "@citation-js/plugin-isbn";
import "@citation-js/plugin-pubmed";
import isEqual from "lodash/isEqual.js";
import moment from "moment";

import {parse as parseDate} from "@citation-js/date";
import {parse as parseName} from "@citation-js/name";
import type {citationData} from "./data/model/index.js";

/**
 * Used in v2 citations.
 */
export const autoCitationsModes = {
	AUTOMATIC: "automatic",
	MANUAL:    "manual"
};

// Keep these in alphabetical order, based on the label
export const citationFormats = [
	{label: "American Medical Association 11th edition", value: "american-medical-association"},
	{label: "APA (7th ed.)", value: "apa"},
	{label: "Bluebook Inline", value: "bluebook-inline"},
	{label: "Bluebook Law Review ", value: "bluebook-law-review"},
	{label: "Chicago Manual of Style 17th edition (author-date)", value: "chicago-author-date"},
	{label: "Chicago Manual of Style 17th edition (note)", value: "chicago-note-bibliography"},
	{label: "Council of Science Editors, Citation-Sequence (numeric, brackets)", value: "council-of-science-editors-brackets"},
	{label: "IEEE (2021)", value: "ieee"},
	{label: "MLA (9th ed.)", value: "modern-language-association"},
	// These two are broken on the API side. A bug in php-citeproc, I think.
	// {label: "Society of Biblical Literature 2nd edition (author-date)", value: "society-of-biblical-literature-author-date"},
	// {label: "Society of Biblical Literature 2nd edition (full note)", value: "society-of-biblical-literature-fullnote-bibliography"},
	{label: "Turabian 9th edition (author-date)", value: "turabian-author-date"}
];

// CSL name types - the ones that are probably more frequently used
export const cslNameTypes = [
	{value: "author", label: "Author"},
	{value: "contributor", label: "Contributor"},
	{value: "editor", label: "Editor"},
	{value: "interviewer", label: "Interviewer"},
	{value: "translator", label: "Translator"}
];

// CSL name types - the ones that are probably less frequently used
export const cslUncommonNameTypes = [
	{value: "chair", label: "Chair"},
	{value: "collection-editor", label: "Collection editor"},
	{value: "compiler", label: "Compiler"},
	{value: "composer", label: "Composer"},
	{value: "container-author", label: "Container author"},
	{value: "curator", label: "Curator"},
	{value: "director", label: "Director"},
	{value: "editor-director", label: "Managing editor"},
	{value: "editor-translator", label: "Editor-translator"},
	{value: "executive-producer", label: "Executive producer"},
	{value: "guest", label: "Guest"},
	{value: "host", label: "Host"},
	{value: "illustrator", label: "Illustrator"},
	{value: "narrator", label: "Narrator"},
	{value: "organizer", label: "Organizer"},
	{value: "original-author", label: "Original creator"},
	{value: "performer", label: "Performer"},
	{value: "producer", label: "Producer"},
	{value: "recipient", label: "Recipient"},
	{value: "reviewed-author", label: "Author of reviewed item"},
	{value: "script-writer", label: "Script writer"},
	{value: "series-creator", label: "Series creator"}
];

/**
 * @deprecated Used in v1 citations.
 */
export const citationTypes = {
	NORMAL:   null,
	FREEFORM: "law", // value is "law" for historical reasons
	PDF:      "pdf"
};

/**
 * @deprecated Used in v1 citations.
 */
export const citationFieldLabels = {
	CITATION_TYPE: "Type",
	ACCESS_DATE:   "Date Accessed",
	PUBLISH_DATE:  "Date Published",
	TITLE:         "Title",
	AUTHOR:        "Author/Contributor",
	PUBLISHER:     "Publisher",
	URL:           "URL",
	FREEFORM:      "Citation",
	DOI:           "DOI"
};

class Citation {

	//
	// PUBLIC
	//

	/**
	 * Get the access date from a citation (of any version) as a Date object.
	 * Returns null if the access date cannot be determined.
	 *
	 * @param {Object} citation
	 * @return {(Date|null)}
	 */
	// @ts-expect-error FIXME: Parameter 'citation' implicitly has an 'any' type.
	getAccessDate(citation) {
		if (this.getCitationVersion(citation) >= 2)
			return this.getV2AccessDate(citation);
		else
			return this.getV1AccessDate(citation);
	};

	/**
	 * @param {Object} cit
	 * @return {number}
	 */
	// @ts-expect-error FIXME: Parameter 'cit' implicitly has an 'any' type.
	getCitationVersion(cit) {
		if (typeof cit !== "object")
			return 0; // unknown

		return ("version" in cit ? cit.version : 1);
	};

	/**
	 * Get the access date from a v2 citation as a Date object.
	 * Returns null if the access date cannot be determined.
	 *
	 * @param {Object} citation
	 * @return {(Date|null)}
	 */
	// @ts-expect-error FIXME: Parameter 'citation' implicitly has an 'any' type.
	getV2AccessDate(citation) {
		let dt = null;
		if ("data" in citation && "zotero" in citation.data && "accessDate" in citation.data.zotero)
			dt = new Date(citation.data.zotero.accessDate);

		// @ts-expect-error TS(2345) FIXME: Argument of type 'Date' is not assignable to param... Remove this comment to see the full error message
		if (dt instanceof Date && !isNaN(dt))
			return dt;
		else
			return null;
	};

	/**
	 * When adding a new annotation to a page, determine (given the existing annotations on the page)
	 * what the citation data should be for the new annotation. If none can be determined,
	 * null is returned.
	 *
	 * @param {Object[]} annotations
	 * @return {(Object|null)}
	 */
	// @ts-expect-error FIXME: Parameter 'annotations' implicitly has an 'any' ty... Remove this comment to see the full error message
	getBulkCitationData(annotations) {
		const v1 = [];
		const v2 = [];
		for (const ann of annotations) {
			if (!("citation" in ann))
				continue;

			// v2 unstructured and v1 law citations are excluded from consideration,
			// as are v1 citations that are not bulk-approved.
			if (this.getCitationVersion(ann.citation) >= 2 && ann.citation.structured === true)
				v2.push(ann);
			else {
				if (ann.doctype !== citationTypes.FREEFORM && "approve_method" in ann.citation && ann.citation.approve_method === "bulk")
					v1.push(ann);
			}
		}

		if (v1.length === 0 && v2.length === 0)
			return null; // shortcut out of here when there are no citations on this page yet.

		// 1. If the page has a bulk-approved v1 citation, use it.
		// otherwise
		// 2. Use the first v2 citation we find, regardless of approval status.

		// NOTE: If the user copied a topic into this project from another one, it's actually possible
		// to have different approved/v2 citations on the same page url. New annotations will just end up with
		// whichever v2 (or approved v1) citation happens to be found first, though when the user opens one
		// and saves it the other ones should be updated.

		let cit = null;
		if (v1.length > 0)
		// @ts-expect-error TS(2554) FIXME: Expected 2 arguments, but got 1.
			cit = this.fromV1(v1[0].citation); // 1: use first bulk-approved v1 citation

		if (cit === null && v2.length > 0) {
			// no approved v1 citation found,
			// but v2 citations available.
			// @ts-expect-error TS(2554) FIXME: Expected 2 arguments, but got 1.
			cit = this.fromV2(v2[0].citation); // 2: use first available v2 citation
		}

		return cit;
	};

	/**
	 * When adding a new annotation to a page, determine (given the existing annotations on the page)
	 * if there are any v1 citations that can be updated. Return information that can be used to update them.
	 *
	 * @param {Object[]} annotations
	 * @param {Object}   citation     The citation to use as a base for newly generated ones
	 * @return {Object[]}
	 */
	// @ts-expect-error FIXME: Parameter 'annotations' implicitly has an 'any' ty... Remove this comment to see the full error message
	getCitationsToUpdate(annotations, citation) {
		if (!citation || this.getCitationVersion(citation) === 1)
			return [];

		const updates = [];
		for (const ann of annotations) {
			if (!("citation" in ann) || this.getCitationVersion(ann.citation) >= 2)
				continue;

			// we have a v1 citation, but exclude law and approved citations.
			if (ann.citation.doctype !== citationTypes.FREEFORM && !ann.citation.approved) {
				const newCit = this.fromV2(citation, ann.citation);

				updates.push({
					annotation_uuid: ann.uuid,
					citation:        newCit
				});
			}
		}

		return updates;
	};

	/**
	 * When updating an existing annotation/citation determine (given the existing annotations on the page)
	 * which existing citations need to be updated (due to bulk update rules).
	 * Return an array of those updated citations (along with the annotation uuid to which they belong).
	 *
	 * @param {string} originatingAnnotationUuid
	 * @param {Object} citation
	 * @param {Object[]} annotations Annotations to consider for updates
	 * @return {Object[]}
	 */
	// @ts-expect-error FIXME: Parameter 'originatingAnnotationUuid' implicitly h... Remove this comment to see the full error message
	calcBulkUpdates(originatingAnnotationUuid, citation, annotations) {
		const changes = [];

		// some helpful consts for what we want to update
		const ALL = "all";
		const SRC_NOTES_ONLY = "source-notes-only";
		const NONE = "none";

		// @ts-expect-error FIXME: Parameter 'cit' implicitly has an 'any' type.
		const shouldUpdate = (cit) => {
			switch (this.getCitationVersion(cit)) {
				case 0:
					// probably a bookmark
					return NONE;
				case 1:
					// don't update law/freeform citations
					return cit.doctype === citationTypes.FREEFORM ? NONE : ALL;
				case 2:
					// fall through
				default: // greater than 2
					// for unstructured/law citations, only update source notes
					return cit.structured ? ALL : SRC_NOTES_ONLY;
			}
		};

		switch (shouldUpdate(citation)) {
			case ALL:
				// Update all aspects of citations for the same source
				// (except page number)

				for (const annotation of annotations) {
					if (annotation.uuid === originatingAnnotationUuid)
						continue;
					if (shouldUpdate(annotation.citation) !== ALL)
						continue;

					let new_citation;
					if (this.getCitationVersion(citation) >= 2)
						new_citation = this.fromV2(citation, annotation.citation);
					else
						new_citation = this.fromV1(citation, annotation.citation);

					changes.push({
						annotation_uuid: annotation.uuid,
						citation:        new_citation
					});
				}
				break;

			case SRC_NOTES_ONLY:
				// Source notes should always be updated for v2+ citations
				// because they apply to the source as a whole, even if
				// (as for law citations) the citations are considered separately.

				for (const annotation of annotations) {
					if (annotation.uuid === originatingAnnotationUuid)
						continue;

					// citation must be v2 or greater,
					// and we can only apply changes to v2 or greater.
					if (this.getCitationVersion(annotation.citation) >= 2) {
						const new_citation = this.updateSourceNotes(citation, annotation.citation);

						changes.push({
							annotation_uuid: annotation.uuid,
							citation:        new_citation
						});
					}
				}

				break;

			case NONE:
				// Nothing should be updated as a result of the changed citation data.
				break;

			default:
				throw new Error("Internal error");
		} // end switch

		return changes;
	};

	//
	// PRIVATE
	//

	/**
	 * Get the access date from a v1 citation as a Date object.
	 * Returns null if the access date cannot be determined.
	 *
	 * @param {Object} citation
	 * @return {(Date|null)}
	 */
	// @ts-expect-error FIXME: Parameter 'citation' implicitly has an 'any' type.
	getV1AccessDate(citation) {
		if (!citation?.data?.Y2)
			return null;

		const Y2 = citation.data.Y2;

		// Try proper RIS datetime format first
		let parts = Y2.split("/");
		if (parts.length === 4) { // 2021/01/02/12:34:56 or maybe 2021/01/02/somethingelse
			const y = parts[0];
			const m = parts[1];
			const d = parts[2];
			const time = parts[3];

			let dt = new Date(`${y}-${m}-${d} ${time}`);
			// @ts-expect-error TS(2345) FIXME: Argument of type 'Date' is not assignable to param... Remove this comment to see the full error message
			if (dt instanceof Date && !isNaN(dt))
				return dt; // success!

			// maybe the date itself is okay and the time is bad?
			// @ts-expect-error TS(2322) FIXME: Type 'string' is not assignable to type 'Date'.
			dt = Date(`${y}-${m}-${d}`);
			// @ts-expect-error TS(2345) FIXME: Argument of type 'Date' is not assignable to param... Remove this comment to see the full error message
			if (dt instanceof Date && !isNaN(dt))
				return dt; // success!

			// give up
			return null;
		}

		// Try W3C time, because sadly we were putting that in Y2 in some circumstances
		parts = Y2.split("T");
		if (parts.length === 2) { // maybe 2021-07-15T00:01:39.839Z
			const [y, m, d] = parts[0].split("-");

			// trim the 'Z' from the end or Bad Things can happen.
			// Also, JS's substr() method is really dumb.
			const time = parts[1].substr(0, parts[1].length - 1);
			const dt = new Date(`${y}-${m}-${d} ${time}`);

			// @ts-expect-error TS(2345) FIXME: Argument of type 'Date' is not assignable to param... Remove this comment to see the full error message
			if (dt instanceof Date && !isNaN(dt))
				return dt; // success!
		}

		// give up
		return null;
	};

	/**
	 * Helper function.
	 * @param {object} citation
	 * @param {object} [original] If passed, used to determine Y2 (access date) for the new citation.
	 * @return object
	 */
	// @ts-expect-error FIXME: Parameter 'citation' implicitly has an 'any' type.
	fromV1(citation, original) {
		// Create a copy so we don't change `annotation.citation.data` inadvertently.
		const data = Object.assign({}, citation.data);

		const now = new Date();
		let Y2 = null;
		if (original === undefined) {
			// Since this is only called when creating new annotations,
			// we can assume that the access date is "now".
			Y2 = RIS.formatDate(now);
		} else {
			const dt = this.getAccessDate(original);
			// @ts-expect-error TS(2345) FIXME: Argument of type 'Date' is not assignable to param... Remove this comment to see the full error message
			if (dt instanceof Date && !isNaN(dt))
				Y2 = RIS.formatDate(dt);
		}

		if (Y2)
			data.Y2 = Y2;
		else
			delete data.Y2;

		const cit = {
			// this is only called when bulk update should be occuring,
			// so "approved" be true, and the method is "bulk".
			approved:       true,
			approve_method: "bulk",

			approve_dt: citation.approve_dt,
			data:       data,
			doctype:    citation.doctype
		};

		// update_dt should be "now" if we're modifying an existing citation,
		// or null if we're creating a new one.
		(cit as any).update_dt = (original === undefined ? null : now.toISOString());

		return cit;
	};

	/**
	 * Helper function.
	 * @param {Object} citation (v2)
	 * @param {object} [original] (v1 or v2 citation) If passed, used to determine Y2 (access date) for the new citation.
	 * @return {Object}
	 */
	// @ts-expect-error FIXME: Parameter 'citation' implicitly has an 'any' type.
	fromV2(citation, original) {
		const cit = cloneDeep(citation);

		let dt;
		if (original === undefined)
			dt = new Date();
		else
			dt = this.getAccessDate(original);

		// Zotero
		// accessDate: "2021-06-25T19:58:16Z"
		if (cit.data.zotero) {
			if (dt)
				cit.data.zotero.accessDate = dt.toISOString();
			else
				delete cit.data.zotero.accessDate;
		}

		// CSL
		// accessed: {
		//	date-parts: [
		//		"2021",
		//		6,
		//		25
		//	]
		// }
		if (cit.data.csl) {
			// TODO: Might need to look at getUTCMonth(), getUTCDate(), etc. depending on where dt originated.
			if (dt) {
				cit.data.csl.accessed = {
					"date-parts": [[
						"" + dt.getFullYear(),
						dt.getMonth() + 1, // JS is insane.
						dt.getDate()
					]]
				};
			} else
				delete cit.data.csl.accessed;
		}

		// RIS
		// Y2: "2021/06/25/19:54:41"
		if (cit.data.ris) {
			if (dt) {
				const Y2 = RIS.formatDate(dt);
				cit.data.ris = RIS.replaceKey("Y2", Y2, cit.data.ris);
			} else
				delete cit.data.ris;
		}

		// pages is unique to each annotation,
		// so should only be set if we're updating an existing citation.
		if (original === undefined || original.pages === undefined)
			delete cit.pages;
		else if (original.pages !== undefined)
			cit.pages = original.pages;

		return cit;
	};

	/**
	 * Helper function.
	 * @param {Object} src (v2 citation)
	 * @param {Object} dst (v2 citation)
	 * @return {Object}
	 */
	// @ts-expect-error FIXME: Parameter 'src' implicitly has an 'any' type.
	updateSourceNotes(src, dst) {
		const cit = cloneDeep(dst);
		const now = new Date();

		if (src.source_notes === undefined) {
			if (dst.source_notes !== undefined) {
				cit.source_notes = [];
				cit.update_dt = now.toISOString();
			}
		} else {
			cit.source_notes = cloneDeep(src.source_notes);
			cit.update_dt = now.toISOString();
		}

		return cit;
	}

	/**
	 * Updates fields for the given v2 citation.
	 *
	 * NOTE: Only meant to be used if the citation extension is not available.
	 *
	 * Replicates the changes in `csl` (needed so we can regenerate the rendered citation HTML)
	 * and `ris` (used as an export).
	 *
	 * @param {{zotero: Object, csl: Object, ris: Object}} data
	 * @param {Object} changes
	 * @param {"webpage"|"encyclopediaArticle"|"journalArticle"|"book"} [changes.itemType]
	 * @param {string} [changes.zotero.title]
	 * @param {string} [changes.zotero.publisher]
	 * @param {string} [changes.zotero.date]
	 * @param {string} [changes.zotero.accessDate]
	 * @param {{creatorType: "author"|"editor"|"contributor"|"translator", firstName: string, lastName: string}[]} [changes.zotero.creators]
	 * @return {Object} updated citation object
	 */
	// @ts-expect-error FIXME
	updateDataManually(data, changes) {
		const updated = cloneDeep(data);

		if ((changes.csl && Object.keys(changes.csl).length === 0)
			&& (changes.zotero && Object.keys(changes.zotero).length === 0))
			return updated;

		if (changes.zotero) {
			// Only if input format for changes is Zotero: convert things to CSL
			updated.zotero = Object.assign({}, data.zotero, changes.zotero);

			if (updated.zotero.itemType === undefined)
				updated.zotero.itemType = "webpage";

			const cslFields = {
				libraryCatalog: "source",
				url:            "URL",
				language:       "language",
				abstractNote:   "abstract"
			};

			// Uf the csl data doesn't exist, construct it so it can be filled in
			if (!updated.csl)
				updated.csl = {};

			Object.keys(cslFields).forEach(source => {
				if (updated.zotero[source] !== undefined)
					// @ts-expect-error FIXME
					updated.csl[cslFields[source]] = updated.zotero[source];
			});

			if (changes.zotero.itemType !== undefined || updated.csl.type === undefined) {
				const typeMapping = {
					"journalArticle":      "article-journal",
					"book":                "book",
					"encyclopediaArticle": "entry-encyclopedia"
				};

				// @ts-expect-error FIXME
				updated.csl.type = typeMapping[updated.zotero.itemType] !== undefined
					// @ts-expect-error FIXME
					? typeMapping[updated.zotero.itemType]
					: "webpage";
			}
			if (changes.zotero.title !== undefined)
				updated.csl.title = changes.zotero.title;
			if (changes.zotero.publisher !== undefined) {
				if (changes.zotero.publisher.trim() === "")
					delete updated.csl.publisher;
				else
					updated.csl.publisher = changes.zotero.publisher;
			}

			const cslDate = (string: string) => {
				const date = new Date(string);
				if (isNaN(date.valueOf()))
					return null;
				return {"date-parts": [["" + date.getFullYear(), date.getMonth() + 1, date.getDate()]]};
			};
			if (changes.zotero.date !== undefined) {
				updated.csl.issued = cslDate(changes.zotero.date);
				if (!updated.csl.issued)
					delete updated.csl.issued;
			}
			if (changes.zotero.accessDate !== undefined) {
				updated.csl.accessed = cslDate(changes.zotero.accessDate);
				if (!updated.csl.accessed)
					delete updated.csl.accessed;
			}

			if (changes.zotero.creators !== undefined) {
				delete updated.csl.author;
				delete updated.csl.editor;
				delete updated.csl.translator;
				changes.zotero.creators
					// @ts-expect-error FIXME
					.filter(creator => ["author", "editor", "translator"].includes(creator.creatorType))
					// @ts-expect-error FIXME
					.forEach(creator => {
						if (updated.csl[creator.creatorType] === undefined)
							updated.csl[creator.creatorType] = [];

						updated.csl[creator.creatorType].push({given: creator.firstName, family: creator.lastName});
					});
			}

			// RIS
			updated.ris = this.CSLtoRIS(updated.csl);

			return updated;
		} else if (changes.csl) {
			// Changes are in CSL format
			if (updated.csl.type === undefined)
				updated.csl.type = "webpage";

			Object.keys(changes.csl).forEach(changedProperty => {
				const change = changes.csl[changedProperty];
				if (typeof change === "string" && change.trim() === "") {
					// Delete empty string properties so they don't show up in the RIS
					delete updated.csl[changedProperty];
					return;
				} else if (changedProperty === "accessed"
					|| changedProperty === "available-date"
					|| changedProperty === "event-date"
					|| changedProperty === "issued"
					|| changedProperty === "original-date"
					|| changedProperty === "submitted"
				) {
					// Delete date properties if they're empty, or RIS conversion will fail
					if (change.raw === "") {
						delete updated.csl[changedProperty];
						return;
					}
				}

				// Otherwise update/add property
				updated.csl[changedProperty] = change;
			});

			return this.fromCSL(updated.csl);
		} else
			// Assume nothing changed
			return data;
	}

	/**
	 * Generates full citation data (Zotero, CSL, and RIS formats) from CSL data.
	 */
	fromCSL(csl: {id?: any; type?: string}): citationData | null {
		if (!csl || Object.keys(csl).length === 0)
			return null;

		delete csl.id;

		return {
			zotero: this.CSLtoZotero(csl),
			csl,
			ris:    this.CSLtoRIS(csl)
		};
	}

	/**
	 * Generates full citation data (Zotero, CSL, and RIS formats) from RIS data.
	 *
	 * @param {string} ris
	 * @return {{zotero: {itemType?: string}, csl: {type?: string}, ris: string}|null}
	 */
	// @ts-expect-error FIXME
	fromRIS(ris) {
		ris = this.cleanUpRIS(ris);
		if (ris === "")
			return null;

		const csl = this.RIStoCSL(ris);

		return {
			zotero: this.CSLtoZotero(csl),
			csl,
			ris
		};
	}

	/**
	 * Generates full citation data (Zotero, CSL, and RIS formats) from a DOI string.
	 *
	 * @param {string} doi E.g. "10.1038/s41467-021-25015-6"
	 * @return {{zotero: {itemType?: string}, csl: {type?: string}, ris: string}|null}
	 */
	// @ts-expect-error FIXME
	async fromDOI(doi) {
		doi = doi.trim();
		if (doi === "")
			return null;

		try {
			const cite = await Cite.async(doi, {forceType: "@doi/id"});
			return this.fromCSL(cite.format("data", {format: "object"})[0]);
		} catch (error) {
			console.error("fromDOI error: ", {error, doi});
			return null;
		}
	}

	/**
	 * Generates full citation data (Zotero, CSL, and RIS formats) from a DOI string.
	 *
	 * @param {string} pmid E.g. "26389227"
	 * @return {Promise<{zotero: {itemType?: string}, csl: {type?: string}, ris: string}|null>}
	 */
	// @ts-expect-error FIXME
	async fromPMID(pmid) {
		try {
			const cite = await Cite.async(pmid, {forceType: "@pubmed/id"});
			return this.fromCSL(cite.format("data", {format: "object"})[0]);
		} catch (error) {
			console.error("fromPMID error: ", {error, pmid});
			return null;
		}
	}

	/**
	 * Generates full citation data (Zotero, CSL, and RIS formats) from an ISBN string.
	 *
	 * @param {string} isbn E.g. "978-0-545-01022-1"
	 * @return {Promise<{zotero: {itemType?: string}, csl: {type?: string}, ris: string}>}
	 */
	// @ts-expect-error FIXME
	async fromISBN(isbn) {
		isbn = isbn.trim();
		if (isbn === "")
			return null;

		let cite;
		try {
			cite = await Cite.async(isbn, {forceType: "@isbn/isbn-10"});
		} catch (error) {
			try {
				cite = await Cite.async(isbn, {forceType: "@isbn/isbn-13"});
			} catch (error) {
				console.error("fromISBN error: ", {error, isbn});
				return null;
			}
		}
		try {
			return this.fromCSL(cite.format("data", {format: "object"})[0]);
		} catch (error) {
			console.error("fromISBN error: ", {error, isbn});
			return null;
		}
	}

	/**
	 * @param {Document} document
	 * @param {string} url Clean URL (may not match the real page's URL). Used only as a fallback.
	 * @return {{zotero: {itemType?: string}, csl: {type?: string}, ris: string}|null}
	 */
	// @ts-expect-error FIXME
	fromMetaTags(document, url) {
		// `<meta name="citation_ISBN" content="1234567890">` => "isbn"
		// @ts-expect-error FIXME
		const getName = element => element.getAttribute("name")
			.trim()
			.replace(/^citation(_|-)?/, "")
			.toLowerCase();

		// `<meta name="citation_title" content=" My Book  ">` => "My Book"
		// @ts-expect-error FIXME
		const getValue = element => element.getAttribute("content")
			.trim();

		// Every value is an array. For example: `{title: ["My Book"], author: ["John Smith", "Mary Smith"]}`.
		const data = Array.from(document.querySelectorAll("meta[name^=citation]"))
			.reduce((acc, e) => {
				const name = getName(e);
				const value = getValue(e);
				if (!value || !name)
					return acc;

				// @ts-expect-error FIXME
				const update = {[name]: acc[name] ? [...acc[name], value] : [value]};
				// @ts-expect-error FIXME
				return Object.assign(acc, update);
			}, {});

		return this._fromMetaTagData(data, url);
	}

	/**
	 * @param {Record<string, string[]>} data
	 * @param {string} fallbackUrl
	 * @return {null|{zotero: {itemType?: string}, csl: {type?: string}, ris: string}}
	 * @private
	 */
	// @ts-expect-error FIXME
	_fromMetaTagData(data, fallbackUrl) {
		// Ensure we have a URL
		for (const field of ["public_url", "fulltext_html_url", "abstract_html_url"]) {
			if (data[field]) {
				if (!data.url)
					data.url = data[field];
				delete data[field];
			}
		}
		if (!data.url)
			data.url = [fallbackUrl];

		// Ensure we have a type
		if (!data.type)
			data.type = ["webpage"];

		const csl = {}; // CSL format citation data
		for (const name of Object.keys(data)) {
			const value = data[name][0], values = data[name];

			try {
				switch (name) {
					case "type":
						// @ts-expect-error FIXME
						csl.type = this.getCSLtype(values);
						break;

					case "title":
					case "abstract":
					case "issue":
					case "volume":
						// @ts-expect-error FIXME
						csl[name] = value;
						break;

					case "url":
						// @ts-expect-error FIXME
						csl.URL = value;
						break;

					case "language":
					case "lang":
						// TODO:
						// Should be entered as an ISO 639-1 two-letter language code (e.g. “en”, “zh”), optionally with a two-letter locale code (e.g. “de-DE”, “de-AT”)
						// @ts-expect-error FIXME
						csl.language = value;
						break;

					case "publisher":
					case "technical_report_institution":
					case "dissertation_institution":
					// @ts-expect-error FIXME
						csl.publisher = value;
						break;

					case "journal_title":
					case "book_title":
					case "conference":
						// @ts-expect-error FIXME
						csl["container-title"] = value;
						break;

					case "publication_date":
					case "date":
						// @ts-expect-error FIXME
						csl.issued = this.getCSLdate(value);
						break;

					case "author":
					case "authors": {
						const tempNames = [];
						for (const v of values)
							// @ts-expect-error FIXME
							tempNames.push(...v.split(";").map(n => n.trim()));
						const names = this.getCSLnames(tempNames);
						// @ts-expect-error FIXME
						csl.author = csl.author ? [...csl.author, ...names] : names;
						break;
					}

					case "firstpage":
					case "lastpage":
					case "page":
						if (data.firstpage && data.lastpage)
							// @ts-expect-error FIXME
							csl.page = `${data.firstpage[0]}-${data.lastpage[0]}`;
						else if (data.page)
							// @ts-expect-error FIXME
							csl.page = data.page[0];
						else if (data.firstpage)
							// @ts-expect-error FIXME
							csl.firstpage = data.firstpage[0];
						else if (data.lastpage)
							// @ts-expect-error FIXME
							csl.lastpage = data.lastpage[0];
						break;
				}
			} catch (e) {
				// Skip any errors on individual fields
			}
		}

		// Required fields
		if (!data.type || !data.title)
			return null;

		return this.fromCSL(csl);
	}

	/**
	 * Converts CSL citation into Zotero format.
	 *
	 * @param {{type?: string}} csl
	 * @return {{itemType?: string}}
	 */
	// @ts-expect-error FIXME
	CSLtoZotero(csl) {
		if (!csl || Object.keys(csl).length === 0)
			return {};

		try {
			const cite = new Cite(csl);

			let zotero = cite.format("zotero-json");
			if (!zotero || zotero.length === 0)
				zotero = {};
			else
				zotero = zotero[0];

			delete zotero.key;
			delete zotero.extra; // The formatter duplicates all the data into this field. Bad formatter.

			// De-dupe authors:
			// The conversion from citation.js sometimes yields duplicate author records in the Zotero data.
			// We will select the ones with the "highest-ranking type" (e.g. "author" is better than "contributor").
			// Order is important and should be kept.
			// @ts-expect-error FIXME
			const withoutType = creator => {
				const ret = Object.assign({}, creator);
				delete ret.creatorType;
				return ret;
			};

			if (zotero.creators && zotero.creators.length > 1) {
				const creators = zotero.creators;
				const typePriorities = {author: 4, editor: 3, translator: 2, contributor: 1};

				for (let i = 0; i < creators.length; i++) {
					if (creators[i].delete)
						continue; // already marked for deletion

					// See if, before this one, another creator is a match. If it is, keep the high-ranking one.
					for (let j = 0; j < i; j++) {
						if (creators[j].delete)
							continue; // already marked for deletion

						if (!isEqual(withoutType(creators[i]), withoutType(creators[j])))
							continue;

						// @ts-expect-error FIXME
						const iPriority = typePriorities[creators[i].creatorType] === undefined
							// @ts-expect-error FIXME
							? 0 : typePriorities[creators[i].creatorType];
						// @ts-expect-error FIXME
						const jPriority = typePriorities[creators[j].creatorType] === undefined
							// @ts-expect-error FIXME
							? 0 : typePriorities[creators[j].creatorType];
						if (iPriority === jPriority || iPriority > jPriority)
							creators[j].delete = true;
						else {
							creators[i].delete = true;
							break;
						}
					}
				}
				// @ts-expect-error FIXME
				zotero.creators = zotero.creators.filter(c => !c.delete);
			}

			return zotero;
		} catch (error) {
			console.error("CSLtoZotero error: ", {error, csl});
			return {};
		}
	}

	/**
	 * Converts CSL citation into RIS format.
	 *
	 * @param {{type?: string}} csl
	 * @return {string}
	 */
	// @ts-expect-error FIXME
	CSLtoRIS(csl) {
		if (!csl || Object.keys(csl).length === 0)
			return "";

		const csl2 = cloneDeep(csl);

		// See: https://docs.citationstyles.org/en/stable/specification.html#date-variables
		const dateVariables = [
			"accessed",
			"available-date",
			"event-date",
			"issued",
			"original-date",
			"submitted"
		];
		// Remove date values that only have "raw" values (the library blows up when trying to generate RIS from those).
		for (const v of dateVariables) {
			if (csl2[v] !== undefined && csl2[v].raw !== undefined && csl2[v]["date-parts"] === undefined)
				delete csl2[v];
		}

		try {
			const cite = new Cite(csl2, {forceType: "@csl/object"});
			return this.cleanUpRIS(cite.format("ris"));
		} catch (error) {
			console.error("CSLtoRIS error: ", {error, csl2});
			return "";
		}
	}

	/**
	 * Converts RIS citation into CSL format.
	 *
	 * @param {string} ris
	 * @return {{type?: string}}
	 */
	// @ts-expect-error FIXME
	RIStoCSL(ris) {
		ris = this.cleanUpRIS(ris);
		if (ris === "")
			return {};

		try {
			const cite = new Cite(ris, {forceType: "@ris/file"});

			const items = cite.format("data", {format: "object"});
			if (items.length === 0)
				return {};

			delete items[0].id;
			return items[0];
		} catch (error) {
			console.error("RIStoCSL error: ", {error, ris});
			return {};
		}
	}

	/**
	 * Makes sure the RIS string is formatted correctly (line endings, etc.),
	 * and that it's not longer than pn-api limit.
	 * Returns an empty string if something is wrong.
	 *
	 * @param {string} ris
	 * @return {string}
	 */
	// @ts-expect-error FIXME
	cleanUpRIS(ris) {
		ris = ris.trim();

		if (ris === "")
			return "";

		const startPos = ris.indexOf("TY  - ");
		if (startPos === -1)
			return "";
		else if (startPos > 0)
			// Sometimes there will be comments (as regular text) before the actual RIS data
			ris = ris.substring(startPos);

		// Fix line endings
		ris = ris.replace(/\r?\n/g, "\r\n") + " \r\n\r\n";

		// Check first/last tags
		if (!ris.startsWith("TY  - ") || !ris.endsWith("ER  - \r\n\r\n"))
			return "";

		// Remove ID field
		// eslint-disable-next-line no-regex-spaces,no-control-regex
		const regExp = new RegExp("\r\nID  - .*\r\n");
		ris = ris.replace(regExp, "\r\n");

		// Remove date ranges or otherwise invalid dates, otherwise they will
		// become weird things in CSL/Zotero
		const dateTags = ["DA", "Y1", "Y2", "PY"];
		dateTags.forEach(tag => {
			let regExp = new RegExp(`(?<=${tag}  - ).*(?=\r\n)`);
			const match = ris.match(regExp);
			if (match?.[0] && !moment(match[0]).isValid()) {
				regExp = new RegExp(`\r\n${tag}  - .*\r\n`);
				ris = ris.replace(regExp, "\r\n");
			}
		});

		// Length check
		const FULL_LIMIT = 4096;
		const FIELD_LIMIT = 2048;
		if (ris.length >= FULL_LIMIT) {
			// Go through the data, line by line, and limit the size of each RIS field.

			// eslint-disable-next-line no-regex-spaces
			const tagRegExp = new RegExp("^(?<tag>[A-Z0-9]{2})  - (?<value>.*)$");

			const sizeByTag = {};
			let skippingTag = false; // If true, ignore subsequent lines until another tag is found
			let currentTag = null, newRis = "";
			for (const line of ris.split("\r\n")) {
				const tagMatch = tagRegExp.exec(line);
				if (tagMatch) {
					skippingTag = false;
					// @ts-expect-error FIXME
					currentTag = tagMatch.groups.tag;
					// @ts-expect-error FIXME
					sizeByTag[currentTag] = tagMatch.groups.value.length;
				} else if (skippingTag)
					continue;
				else {
				// @ts-expect-error FIXME
					if ((sizeByTag[currentTag] + ("\r\n" + line).length) > FIELD_LIMIT) {
						skippingTag = true;
						continue; // Skip this line
					}

					// @ts-expect-error FIXME
					sizeByTag[currentTag] += ("\r\n" + line).length;
				}
				newRis += line + "\r\n";
			}
			ris = newRis.substring(0, newRis.length - 2);

			if (ris.length >= FULL_LIMIT) {
				// Full length still over the limit, let's start removing fields (the largest ones go first)

				const tagsToRemove = Object.keys(sizeByTag)
					// Ignore important tags
					.filter(tag => !["TY", "ER"].includes(tag))
					// Sort by size, descending
					// @ts-expect-error FIXME
					.sort((a, b) => sizeByTag[b] - sizeByTag[a]);

				const prevTotalLength = ris.length;
				while (ris.length > FULL_LIMIT) {
					const tag = tagsToRemove.shift();

					let currentTag = null, newRis = "";
					for (const line of ris.split("\r\n")) {
						const tagMatch = tagRegExp.exec(line);
						if (tagMatch)
							// @ts-expect-error FIXME
							currentTag = tagMatch.groups.tag;
						if (currentTag === tag)
							continue;
						newRis += line + "\r\n";
					}
					ris = newRis.substring(0, newRis.length - 2);

					if (prevTotalLength === ris.length)
						break; // prevent infinite loop
				}
			}
		}

		return ris;
	}

	/**
	 * @param {string} url
	 * @return {boolean}
	 */
	// @ts-expect-error FIXME
	isAutoCitationBlacklisted(url) {
		if (!url)
			return false;

		const hosts = [
			"powernotes.com",
			"app.powernotes.com",
			"demo.powernotes.com",
			"app.demo.powernotes.com",
			"localhost:10000"
		];

		try {
			const parsedUrl = new URL(url);
			return hosts.includes(parsedUrl.host.toLowerCase());
		} catch (e) {
			// Bad URL
			return false;
		}
	}

	/**
	 * Given a date string, returns a date object for CSL format.
	 *
	 * @param {string} dateString
	 * @return {Object} Property will either be date-parts or raw
	 */
	// @ts-expect-error FIXME
	getCSLdate(dateString) {
		// Citation.js can't handle dates such as "Jul 8, 2022" (e.g. from ProQuest),
		// which leads to an error in the RIS export, so we'll handle this case here.
		const match = /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec|January|February|March|April|June|July|August|September|October|November|December) (\d{1,2}), (\d{4})$/i.exec(dateString);
		if (match) {
			const date = new Date(dateString);
			return {"date-parts": [[date.getFullYear(), date.getMonth() + 1, date.getDate()]]};
		}

		return parseDate(dateString);
	}

	/**
	 * Given an array of names, returns an array of names in CSL format.
	 *
	 * @param {string[]} names
	 * @return {Object[]} Possible properties of each name object: given, family, suffix, dropping-particle, non-dropping-particle
	 */
	// @ts-expect-error FIXME
	getCSLnames(names) {
		const urlRegex = /(?:(?:https?|ftp):\/\/|\b(?:[a-z\d]+\.))(?:(?:[^\s()<>]+|\((?:[^\s()<>]+|(?:\([^\s()<>]+\)))?\))+(?:\((?:[^\s()<>]+|(?:\(?:[^\s()<>]+\)))?\)|[^\s`!()\[\]{};:'".,<>?«»“”‘’]))?/;

		const results = [];
		for (let name of names) {
			// Remove any URLs in the name, because for some reason some names come with URLs
			name = name.replace(urlRegex, "").trim();

			// Add it to the authors array
			results.push(parseName(name));
		}

		return results;
	}

	/**
	 * Given an array of types, makes a best guess for a CSL type and returns it.
	 *
	 * @param {string[]} types strings to make a guess for a type based off of,
	 *                         in decreasing importance/specificity
	 * @param {string}   fallbackType type to assign if a guess can't be made
	 * @return {string}
	 */
	// @ts-expect-error FIXME
	getCSLtype(types, fallbackType = "webpage") {
		// TODO: expand the types guessed
		for (const arrType of types) {
			const type = arrType.toLowerCase();
			if (type.includes("newspaper")) {
				// Ex. Newspaper
				return "article-newspaper";
			} else if (type.includes("journal")) {
				// Ex. Scholarly Journal, Journal Article
				return "article-journal";
			} else if (type.includes("book review")) {
				// Ex. Book Review
				return "review-book";
			} else if (type.includes("letter")) {
				// Ex. Letter To The Editor
				return "personal_communication";
			}
		}

		return fallbackType;
	}

	/**
	 * Given a string, generates full citation data (Zotero, CSL, and RIS
	 * formats) if it's recognized as a DOI, ISBN or PMID.
	 *
	 * @param {string} value
	 * @return {Object}
	 */
	// @ts-expect-error FIXME
	async search(value) {
		value = value.trim();
		if (value === "")
			return null;

		let cite;
		try {
			cite = await Cite.async(value);
			return this.fromCSL(cite.format("data", {format: "object"})[0]);
		} catch (error) {
			// Try searching DOI, PMID and ISBN one by one (sometimes the general
			// search fails, while the forced-type ones don't)

			// Start with DOI
			try {
				cite = await this.fromDOI(value);
				if (cite === null)
					throw new Error("Error searching DOI");
			} catch (e) {
				// Then try PMID
				try {
					cite = await this.fromPMID(value);
					if (cite === null)
						throw new Error("Error searching PMID");
				} catch (e) {
					// Finally try ISBN
					try {
						cite = await this.fromISBN(value);
						if (cite === null)
							throw new Error("Error searching ISBN");
					} catch (e) {
						// Okay, maybe something really is wrong
						throw new Error(`Error searching for ${value}`);
					}
				}
			} finally {
				// eslint-disable-next-line no-unsafe-finally
				return cite;
			}
		}
	}
}

export default new Citation();
